import { getAccountsFromNamespaces } from "@walletconnect/utils";
import { db, coerceChainId } from "../utils.mjs";

import {
  getChainFromId,
  getChainRpcUrls,
  buildWalletAddEthereumChainParams,
} from "./config.mjs";

import { ensureMathBigIntSafe, clearWalletConnectStorage } from "../patchMathBigInt.mjs";
import { enableWithUriDiagnostics } from "./wcDiagnostics.mjs";
import { buildAppKitThemeOptions } from "./wcTheme.mjs";
import { isWalletUserCancel } from "../walletCancel.mjs";
import { ensureAppKitLayoutStyles, stopAppKitUiEnhancements } from "../appkitUiEnhance.mjs";
import { cleanupAppKitDom, resetAppKitCssState } from "../appkitCleanup.mjs";
import { ensureW3mModalHost } from "../appkitModalRepair.mjs";

const PROJECT_ID = "fbdb8e9cdd25c6f9e115acca94322b3c";
const DEFAULT_RELAY = "wss://relay.walletconnect.com";
const PRODUCTION_METADATA_URL = "https://hieswap.com";

/** Bridge 支持的 EVM 链（WC / 跨链 / 授权共用） */
export const EVM_WC_CHAIN_IDS = [56, 201022, 1, 137];
const EVM_CHAIN_IDS = EVM_WC_CHAIN_IDS;

/** targetChainId 无效时的 fallback（单链 proposal 仍以用户选择的源链为准） */
const WC_ANCHOR_CHAIN_ID = 56;

function buildRpcMapForChain(chainId) {
  const id = coerceChainId(chainId, WC_ANCHOR_CHAIN_ID);
  const map = {};
  const urls = getChainRpcUrls(getChainFromId(id));
  if (urls[0]) map[String(id)] = urls[0];
  return map;
}

function normalizeAccount(account) {
  if (!account) return "";
  if (String(account).includes(":")) {
    const parts = String(account).split(":");
    return parts[parts.length - 1] ?? "";
  }
  return String(account);
}

function sleepMs(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function getWcSessionTopic(provider) {
  return (
    provider?.session?.topic ||
    provider?.signer?.session?.topic ||
    provider?.client?.session?.topic ||
    ""
  );
}

/** signer.session 往往比 provider.session 更早就绪（AVE 扫码后常见） */
function getWcEip155Namespace(provider) {
  return (
    provider?.session?.namespaces?.eip155 ||
    provider?.signer?.session?.namespaces?.eip155 ||
    provider?.signer?.namespaces?.eip155 ||
    null
  );
}

/** session 里原始 CAIP 账户（不受 provider.chainId 过滤影响） */
function getRawWcCaipAccounts(provider) {
  const ns = getWcEip155Namespace(provider);
  if (ns?.accounts?.length) return ns.accounts.map(String);
  const session = provider?.session || provider?.signer?.session;
  if (!session?.namespaces) return [];
  try {
    const list = getAccountsFromNamespaces(session.namespaces, ["eip155"]);
    return Array.isArray(list) ? list.map(String) : [];
  } catch (_e) {
    return [];
  }
}

/**
 * AVE 等钱包常只授权 FSC，而 init chains=[BSC] 时 SDK 的 setAccounts 会按 chainId 过滤，
 * 导致 provider.accounts 为空但 session 里已有地址 — 从 session 回填。
 */
function hydrateProviderAccountsFromSession(provider, targetChainId) {
  if (!provider) return "";
  const raw = getRawWcCaipAccounts(provider);
  const target = coerceChainId(targetChainId, 0);
  let pick = "";
  if (raw.length) {
    pick = target ? raw.find((a) => String(a).startsWith(`eip155:${target}:`)) || "" : "";
    if (!pick && !target) pick = raw[0];
  }
  const account = pick ? normalizeAccount(pick) : "";
  if (account && (!Array.isArray(provider.accounts) || !provider.accounts.length)) {
    provider.accounts = [account];
  }
  return account || extractWalletConnectAccount(provider, targetChainId);
}

/** 从已建立的 WC session 读取账户（不依赖 enable() 返回） */
export function extractWalletConnectAccount(provider, targetChainId) {
  const raw = getRawWcCaipAccounts(provider);
  const target = coerceChainId(targetChainId, 0);
  if (target && raw.length) {
    const match = raw.find((a) => String(a).startsWith(`eip155:${target}:`));
    if (match) return normalizeAccount(match);
  }
  if (raw[0]) return normalizeAccount(raw[0]);
  const fromProvider = provider?.accounts?.[0];
  if (fromProvider) return normalizeAccount(fromProvider);
  return "";
}

/** session 已授权目标链且能解析账户时，组装连接结果 */
export function buildWalletConnectConnectResult(provider, targetChainId) {
  const target = coerceChainId(targetChainId, WC_ANCHOR_CHAIN_ID);
  if (!isEvmWalletConnectSessionLive(provider) || !wcSessionHasChain(provider, target)) {
    return null;
  }
  const account = hydrateProviderAccountsFromSession(provider, targetChainId);
  if (!account) return null;
  if (provider) provider.chainId = target;
  const topic = getWcSessionTopic(provider);
  if (topic) db.set("topic", topic);
  pinActiveWalletConnectProvider(provider);
  return { provider, account, chainId: target };
}

async function probeWalletConnectAccounts(provider, timeoutMs = 8000) {
  if (!provider?.request) return "";
  try {
    const accs = await Promise.race([
      provider.request({ method: "eth_accounts" }),
      sleepMs(timeoutMs).then(() => []),
    ]);
    return normalizeAccount(accs?.[0]);
  } catch (_e) {
    return "";
  }
}

/** 手机已授权后，轮询 session / eth_accounts 直到拿到地址 */
export async function waitForWalletConnectSessionAccounts(provider, targetChainId, options = {}) {
  const timeoutMs = options.timeoutMs ?? 45000;
  const skipRpcProbe = options.skipRpcProbe === true;
  const onAccount = options.onAccount;
  const deadline = Date.now() + timeoutMs;
  const rpcProbeAfter = Date.now() + Math.min(8000, Math.floor(timeoutMs * 0.35));
  while (Date.now() < deadline) {
    const built = buildWalletConnectConnectResult(provider, targetChainId);
    if (built?.account) {
      onAccount?.(built.account);
      return built;
    }
    /* enable() 未完成时 eth_accounts 可能与 eth_requestAccounts 互斥，session 优先 */
    if (!skipRpcProbe && Date.now() >= rpcProbeAfter) {
      const probed = await probeWalletConnectAccounts(provider, 2500);
      if (probed) {
        const built = buildWalletConnectConnectResult(provider, targetChainId);
        if (built?.account) {
          onAccount?.(built.account);
          return built;
        }
      }
    }
    await sleepMs(350);
  }
  return null;
}

async function requestWithTimeout(promise, ms, code = "WC_REQUEST_TIMEOUT") {
  return Promise.race([
    promise,
    sleepMs(ms).then(() => {
      const err = new Error(code);
      err.code = code;
      throw err;
    }),
  ]);
}

/**
 * Reown Cloud 校验页面 origin；本地 dev 须把 http://localhost:8080 加入 project Allowed Domains。
 * metadata.url 必须与当前页面 origin 一致，否则 SDK 告警且 relay 可能拒绝连接。
 */
function resolveWalletConnectMetadata() {
  const origin =
    typeof window !== "undefined" ? window.location.origin : PRODUCTION_METADATA_URL;
  const isLocalDev =
    typeof window !== "undefined" &&
    /^https?:\/\/(localhost|127\.0\.0\.1|\d{1,3}(?:\.\d{1,3}){3})(:\d+)?$/i.test(origin);
  return {
    url: origin,
    icons: [`${PRODUCTION_METADATA_URL}/favicon.ico`],
    isLocalDev,
    pageOrigin: origin,
  };
}

/** 严格单链：QR proposal 仅包含源链，不把 BSC 等放进 optional/rpcMap（手机端否则会显示 BSC） */
export function buildWalletConnectNamespaceChains(targetId) {
  const id = coerceChainId(targetId, WC_ANCHOR_CHAIN_ID);
  return { requiredChains: [id], optional: [] };
}

/** 持久化 shell 的 session 是否已授权目标源链（复用/恢复判断用，不看 provider.chainId） */
export function wcShellAuthorizesChain(provider, chainId) {
  if (!provider || !isEvmWalletConnectSessionLive(provider)) return false;
  const target = coerceChainId(chainId, 0);
  if (!target) return false;
  return wcSessionHasChain(provider, target);
}

/** WC session 是否已授权某条 EVM 链（发 eth_sendTransaction 前必须满足） */
export function wcSessionHasChain(provider, chainId) {
  const target = coerceChainId(chainId, 0);
  if (!target) return false;
  return getWalletConnectSessionChainIds(provider).includes(target);
}

export function assertWcChainAuthorized(provider, chainId) {
  const target = coerceChainId(chainId, WC_ANCHOR_CHAIN_ID);
  if (wcSessionHasChain(provider, target)) return target;
  const approved = getWalletConnectSessionChainIds(provider);
  const chain = getChainFromId(target);
  const err = new Error("WC_CHAIN_NOT_IN_SESSION");
  err.code = "WC_CHAIN_NOT_IN_SESSION";
  err.chainId = target;
  err.chainName = chain?.name || String(target);
  err.approvedNames = approved
    .map((id) => getChainFromId(id)?.name || String(id))
    .join(", ");
  throw err;
}

export function getWalletConnectSessionChainIds(provider) {
  const ns = getWcEip155Namespace(provider);
  if (!ns) return [];
  const caip = new Set(ns.chains || []);
  for (const acc of ns.accounts || []) {
    const parts = String(acc).split(":");
    if (parts.length >= 2) caip.add(`${parts[0]}:${parts[1]}`);
  }
  return [...caip].map((c) => coerceChainId(c, 0)).filter((n) => n > 0);
}

export async function ensureWalletConnectOnTargetChain(provider, targetChainId, options = {}) {
  const target = coerceChainId(targetChainId, WC_ANCHOR_CHAIN_ID);
  if (!EVM_CHAIN_IDS.includes(target)) {
    throw new Error(`WalletConnect unsupported chain: ${targetChainId}`);
  }
  const force = options.force === true;
  if (
    !force &&
    wcChainOk.provider === provider &&
    wcChainOk.chainId === target &&
    Date.now() - wcChainOk.at < 20000
  ) {
    provider.chainId = target;
    return target;
  }

  if (wcChainEnsureInflight) {
    await wcChainEnsureInflight;
    if (wcChainOk.provider === provider && wcChainOk.chainId === target) {
      provider.chainId = target;
      return target;
    }
  }

  const ensureTask = async () => {
  const readWalletChainId = async () => {
    if (wcTxInflight) {
      return coerceChainId(provider?.chainId, 0);
    }
    try {
      const raw = await wcSafeRequest(provider, { method: "eth_chainId" });
      return coerceChainId(raw, 0);
    } catch (_e) {
      return coerceChainId(provider?.chainId, 0);
    }
  };

  let current = await readWalletChainId();

  let approved = getWalletConnectSessionChainIds(provider);
  const hex = "0x" + target.toString(16);

  if (!approved.includes(target)) {
    const addParams = buildWalletAddEthereumChainParams(target);
    if (addParams) {
      try {
        console.info("[WC:evm] wallet_addEthereumChain", { chainId: target });
        await requestWithTimeout(
          wcSafeRequest(provider, {
            method: "wallet_addEthereumChain",
            params: [addParams],
          }),
          30000,
          "WC_ADD_CHAIN_TIMEOUT",
        );
      } catch (addErr) {
        console.warn("[WC:evm] wallet_addEthereumChain", addErr?.code || addErr?.message || addErr);
      }
    }
    approved = getWalletConnectSessionChainIds(provider);
    if (!approved.includes(target)) {
      assertWcChainAuthorized(provider, target);
    }
  }

  if (current === target) {
    provider.chainId = target;
    wcChainOk.provider = provider;
    wcChainOk.chainId = target;
    wcChainOk.at = Date.now();
    return target;
  }

  console.info("[WC:evm] wallet_switchEthereumChain", { from: current, to: target });
  try {
    await requestWithTimeout(
      wcSafeRequest(provider, {
        method: "wallet_switchEthereumChain",
        params: [{ chainId: hex }],
      }),
      30000,
      "WC_SWITCH_TIMEOUT",
    );
  } catch (switchErr) {
    console.warn("[WC:evm] wallet_switchEthereumChain", switchErr?.code || switchErr?.message || switchErr);
  }

  const deadline = Date.now() + 12000;
  while (Date.now() < deadline) {
    if (wcTxInflight) break;
    current = await readWalletChainId();
    if (current === target) {
      provider.chainId = target;
      wcChainOk.provider = provider;
      wcChainOk.chainId = target;
      wcChainOk.at = Date.now();
      console.info("[WC:evm] wallet on target chain", { chainId: target });
      return target;
    }
    await sleepMs(400);
  }

  if (!wcTxInflight) {
    current = await readWalletChainId();
    if (current === target) {
      provider.chainId = target;
      wcChainOk.provider = provider;
      wcChainOk.chainId = target;
      wcChainOk.at = Date.now();
      return target;
    }
  }

  const chain = getChainFromId(target);
  const walletChain = getChainFromId(current);
  const err = new Error("WC_WALLET_WRONG_CHAIN");
  err.code = "WC_WALLET_WRONG_CHAIN";
  err.chainId = target;
  err.chainName = chain?.name || String(target);
  err.walletChainId = current;
  err.walletChainName = walletChain?.name || String(current || "unknown");
  throw err;
  };

  wcChainEnsureInflight = ensureTask();
  try {
    return await wcChainEnsureInflight;
  } finally {
    wcChainEnsureInflight = null;
  }
}

async function loadEthereumProvider() {
  ensureMathBigIntSafe();
  const { default: EthereumProvider } = await import("@walletconnect/ethereum-provider");
  return EthereumProvider;
}

/** wcBasic 模式 ModalController.open 不 await 完整 prefetch；勿篡改 state.count（会与链过滤不一致导致 All Wallets 满屏灰块） */
async function prefetchAppKitWalletExplorer() {
  try {
    const { ApiController } = await import("@reown/appkit-controllers");
    await ApiController.prefetch();
  } catch (err) {
    console.warn("[WC] AppKit wallet explorer prefetch failed", err?.message || err);
  }
}

/** @type {import('@walletconnect/ethereum-provider').default | null} */
let activeProvider = null;
/** 页面级单例：避免重复 init 导致 AppKit 第二次不弹窗 */
let wcProviderShell = null;
/** 并发 init 合并为一次 EthereumProvider.init() */
let wcInitInflight = null;
/** 进行中的 provider.enable()；dispose 前必须 await，否则二次扫码会 Connection request reset */
let wcEnableInflight = null;
/** 串行化配对入口（connectEvmWalletConnect / beginWalletConnectPairing） */
let wcPairingTail = Promise.resolve();
/** dispose 后递增，丢弃旧 provider 上迟到的 display_uri */
let wcProviderGeneration = 0;
/** 串行化 WC JSON-RPC，避免 eth_sendTransaction 与 eth_chainId 并发导致 ObjectMultiplex 崩溃 */
let wcRequestTail = Promise.resolve();
let wcTxInflight = false;
/** 切链验证缓存（同一 provider 20s 内不重复 switch + poll） */
const wcChainOk = { provider: null, chainId: 0, at: 0 };
let wcChainEnsureInflight = null;
/** 桌面 AppKit：Rosswap activate 代数，新点击作废旧 activate */
let desktopActivateGen = 0;
/** 安装引导内嵌 QR 代数，切到桌面 WC 弹窗时作废排队中的 inline pairing */
let inlinePairingGen = 0;
/** @type {Promise<unknown> | null} */
let desktopModalInflight = null;
/** 同链同端 coalesce 连点 WC，避免重复 enable / 重复 pairing URI */
let desktopModalInflightKey = "";
/** 串行 desktop activate，避免并发 teardown/enable 互踩 */
let desktopActivateQueue = Promise.resolve();

export function supersedeDesktopWalletConnectModal() {
  desktopActivateGen += 1;
  desktopModalInflight = null;
  desktopModalInflightKey = "";
}

/** 复用 shell 时 SDK 有时不自动 reopen AppKit */
function openAppKitModalIfNeeded(provider) {
  if (!provider?.rpc?.showQrModal) return;
  try {
    provider.modal?.open?.();
  } catch (_e) {
    void _e;
  }
}

/** 用户取消 / 重试：尽快中止进行中的 enable，避免二次打开卡在 loading */
export async function forceAbortWalletConnectAttempt(options = {}) {
  const { closeModal = true, timeoutMs = 800 } = options;
  supersedeDesktopWalletConnectModal();
  supersedeInlineWalletConnectPairing();
  const p = activeProvider || wcProviderShell;
  if (p) abortActivePairing(p, { closeModal });
  if (wcEnableInflight) {
    try {
      await Promise.race([wcEnableInflight, sleepMs(timeoutMs)]);
    } catch (_e) {
      void _e;
    }
    wcEnableInflight = null;
  }
}

export function supersedeInlineWalletConnectPairing() {
  inlinePairingGen += 1;
}

export function isDesktopWalletConnectModalBusy() {
  return !!desktopModalInflight;
}

export function isWcTxInflight() {
  return wcTxInflight;
}

/** 串行化 WC provider.request；发 tx 期间禁止插入 eth_chainId/eth_call */
export async function wcSafeRequest(provider, payload) {
  if (!provider?.request) {
    const err = new Error("WC_NO_PROVIDER");
    err.code = "WC_NO_PROVIDER";
    throw err;
  }
  const method = String(payload?.method || "");
  const isSendTx = method === "eth_sendTransaction";

  const run = async () => {
    if (isSendTx) wcTxInflight = true;
    try {
      return await provider.request(payload);
    } finally {
      if (isSendTx) wcTxInflight = false;
    }
  };

  const queued = wcRequestTail.then(run, run);
  wcRequestTail = queued.then(
    () => {},
    () => {},
  );
  return queued;
}

function abortActivePairing(provider, options = {}) {
  if (!provider) return;
  const { closeModal = false } = options;
  /* SDK connect() 订阅 modal 状态：close() 后 !open 且无 session 会抛 Connection request reset */
  if (closeModal) {
    try {
      provider.modal?.close?.();
    } catch (_e) {
      void _e;
    }
  }
  try {
    provider.signer?.abortPairingAttempt?.();
  } catch (_e) {
    void _e;
  }
}

function enqueueWcPairing(fn) {
  const job = wcPairingTail.then(fn, fn);
  wcPairingTail = job.then(
    () => {},
    () => {},
  );
  return job;
}

/** 等 init / enable / 桌面 activate / 内联配对队列 收尾后再 dispose 或开新一轮配对 */
export async function awaitWalletConnectIdle(options = {}) {
  const timeoutMs = options.timeoutMs ?? 4000;
  if (wcInitInflight) {
    try {
      await Promise.race([wcInitInflight, sleepMs(timeoutMs)]);
    } catch (_e) {
      void _e;
    }
  }
  if (wcEnableInflight) {
    try {
      await Promise.race([wcEnableInflight, sleepMs(timeoutMs)]);
    } catch (_e) {
      void _e;
    }
  }
  try {
    await Promise.race([desktopActivateQueue, sleepMs(timeoutMs)]);
  } catch (_e) {
    void _e;
  }
  try {
    await Promise.race([wcPairingTail, sleepMs(timeoutMs)]);
  } catch (_e) {
    void _e;
  }
}

/** 无 live session 时可复用的 provider shell（避免 AppKit isInitialized 二次 init 失效） */
function pickCancelledWcShell(targetChainId) {
  const shell = wcProviderShell;
  if (!shell || isEvmWalletConnectSessionLive(shell)) return null;
  const id = coerceChainId(targetChainId, WC_ANCHOR_CHAIN_ID);
  const shellChain = coerceChainId(shell.chainId, 0);
  if (shellChain && shellChain !== id) return null;
  return shell;
}

/** 安装引导内嵌 QR：软中止 pairing，保留 shell + w3m-modal 宿主 */
async function teardownForInlineWalletConnect(options = {}) {
  const { targetChainId, forceFullReset = false } = options;
  detachEvmWalletConnectListeners();
  clearWalletConnectDisplayUriConsumer();

  const reusable = !forceFullReset && pickCancelledWcShell(targetChainId);
  if (reusable) {
    wcProviderGeneration += 1;
    if (wcEnableInflight) {
      try {
        await Promise.race([wcEnableInflight, sleepMs(3500)]);
      } catch (_e) {
        void _e;
      }
      wcEnableInflight = null;
    }
    abortActivePairing(reusable, { closeModal: false });
    activeProvider = reusable;
    wcProviderShell = reusable;
    if (reusable.rpc) reusable.rpc.showQrModal = false;
    stopAppKitUiEnhancements();
    resetAppKitCssState();
    await ensureW3mModalHost();
    return reusable;
  }

  await disposeActiveProvider({ resetInit: true, removeDom: false, closeModal: false });
  await ensureW3mModalHost();
  return null;
}

/** @type {((uri: string) => void) | null} */
let displayUriConsumer = null;

export function clearWalletConnectDisplayUriConsumer() {
  displayUriConsumer = null;
}

function setDisplayUriConsumer(fn) {
  displayUriConsumer = typeof fn === "function" ? fn : null;
  if (!displayUriConsumer || typeof window === "undefined") return;
  const last = String(window.__hieswapWcLastUri || "").trim();
  const lastGen = Number(window.__hieswapWcLastUriGen || 0);
  if (last && lastGen === wcProviderGeneration) {
    displayUriConsumer(last);
  }
}

let evmListenerUnsubs = [];

function attachEvmWcListeners(provider, callbacks = {}) {
  for (const off of evmListenerUnsubs) {
    try {
      off();
    } catch (_e) {
      void _e;
    }
  }
  evmListenerUnsubs = [];
  if (!provider || typeof provider.on !== "function") return;

  const onAccounts = provider.on("accountsChanged", (accounts) => {
    const acc = Array.isArray(accounts) ? accounts[0] : accounts;
    callbacks.onAccountsChanged?.(acc);
  });
  if (typeof onAccounts === "function") evmListenerUnsubs.push(onAccounts);

  const onChain = provider.on("chainChanged", (hex) => {
    callbacks.onChainChanged?.(hex);
  });
  if (typeof onChain === "function") evmListenerUnsubs.push(onChain);

  const onDisconnect = provider.on("disconnect", () => {
    callbacks.onDisconnect?.();
  });
  if (typeof onDisconnect === "function") evmListenerUnsubs.push(onDisconnect);
}

export function detachEvmWalletConnectListeners() {
  for (const off of evmListenerUnsubs) {
    try {
      off();
    } catch (_e) {
      void _e;
    }
  }
  evmListenerUnsubs = [];
}

export function bindEvmWalletConnectListeners(callbacks = {}) {
  const p = activeProvider || wcProviderShell;
  if (!p) return;
  attachEvmWcListeners(p, callbacks);
}

/** WC 会话是否仍可用于 eth_sendTransaction / personal_sign */
export function isEvmWalletConnectSessionLive(provider) {
  if (!provider || typeof provider.request !== "function") return false;
  const topic =
    provider?.session?.topic ||
    provider?.signer?.session?.topic ||
    provider?.client?.session?.topic;
  return Boolean(String(topic || "").trim());
}

/** 页面级 WC provider 单例（含活跃 session） */
export function getActiveWalletConnectProvider() {
  const p = activeProvider || wcProviderShell;
  return isEvmWalletConnectSessionLive(p) ? p : null;
}

/**
 * 发交易/签名前解析可用 WC provider。
 * store 中的引用可能在 dispose 后 session 已失效，需回落到 wcProviderShell。
 */
export function resolveEvmWalletConnectProvider(fallback) {
  const live = getActiveWalletConnectProvider();
  if (live) return live;
  if (isEvmWalletConnectSessionLive(fallback)) return fallback;
  const err = new Error("WC_SESSION_NOT_READY");
  err.code = "WC_SESSION_NOT_READY";
  throw err;
}

/** 将 live session topic 写回 db，避免 store 里仍是旧 topic 导致 Pending session not found */
export function syncWcSessionTopic(provider) {
  const topic = getWcSessionTopic(provider);
  if (topic) {
    db.set("topic", topic);
    pinActiveWalletConnectProvider(provider);
  }
  return topic;
}

function assertWcSessionTopicValid(provider) {
  const topic = getWcSessionTopic(provider);
  if (!topic) {
    const err = new Error("WC_SESSION_NOT_READY");
    err.code = "WC_SESSION_NOT_READY";
    throw err;
  }
  const stored = String(db.get("topic", "") || "").trim();
  if (stored && stored !== topic) {
    console.warn("[WC:evm] topic mismatch, syncing to live session", {
      stored: stored.slice(0, 12),
      live: topic.slice(0, 12),
    });
    db.set("topic", topic);
  }
  return topic;
}

/**
 * 授权/跨链发 eth_sendTransaction 前：session 存活 + topic 一致 + 目标链在 session 里。
 * 不尝试切链——手机钱包 switchEthereumChain 响应成功但实际仍用旧链广播，不可靠。
 * 链不匹配 → 明确报错，让用户断开重扫正确源链。
 */
export async function ensureWcSessionReadyForTx(provider, chainId) {
  const wc = resolveEvmWalletConnectProvider(provider);
  if (!isEvmWalletConnectSessionLive(wc)) {
    const err = new Error("WC_SESSION_NOT_READY");
    err.code = "WC_SESSION_NOT_READY";
    throw err;
  }
  assertWcSessionTopicValid(wc);
  pinActiveWalletConnectProvider(wc);
  assertWcChainAuthorized(wc, chainId);
  return wc;
}

function pinActiveWalletConnectProvider(provider) {
  if (!provider) return;
  wcProviderShell = provider;
  activeProvider = provider;
}

async function disposeActiveProvider(options = {}) {
  const { resetInit = false, removeDom = false, keepSession = false, closeModal = false } = options;
  if (keepSession) {
    clearWalletConnectDisplayUriConsumer();
    stopAppKitUiEnhancements();
    if (removeDom) cleanupAppKitDom();
    else resetAppKitCssState();
    if (wcProviderShell) activeProvider = wcProviderShell;
    return;
  }
  const run = async () => {
    detachEvmWalletConnectListeners();
    clearWalletConnectDisplayUriConsumer();
    wcProviderGeneration += 1;
    const p = activeProvider || wcProviderShell;
    activeProvider = null;
    stopAppKitUiEnhancements();
    if (p) {
      if (wcEnableInflight) {
        try {
          await Promise.race([wcEnableInflight, sleepMs(3500)]);
        } catch (_e) {
          void _e;
        }
        wcEnableInflight = null;
      }
      abortActivePairing(p, { closeModal });
      try {
        await Promise.race([
          p.disconnect(),
          new Promise((resolve) => setTimeout(resolve, 2500)),
        ]);
      } catch (_e) {
        void _e;
      }
    }
    if (removeDom) {
      cleanupAppKitDom();
    } else {
      resetAppKitCssState();
    }
    if (resetInit) {
      wcProviderShell = null;
      await sleepMs(200);
      await clearWalletConnectStorage();
    }
  };
  await run();
}

function bindProviderDisplayUri(provider, options = {}) {
  provider.__hieswapDisplayUriGen = wcProviderGeneration;
  if (!provider.__hieswapDisplayUriBound) {
    provider.__hieswapDisplayUriBound = true;
    provider.on("display_uri", (uri) => {
      if (provider.__hieswapDisplayUriGen !== wcProviderGeneration) return;
      console.info(`[WC:evm] display_uri ${String(uri || "").slice(0, 48)}…`);
      if (typeof window !== "undefined") {
        window.__hieswapWcLastUri = String(uri || "");
        window.__hieswapWcLastUriGen = wcProviderGeneration;
      }
      displayUriConsumer?.(uri);
    });
  }
  setDisplayUriConsumer(options.onDisplayUri);
}

function bindProviderSessionCallbacks(provider, options = {}) {
  if (!provider || typeof provider.on !== "function") return;
  provider.__hieswapSessionOpts = options;
  if (provider.__hieswapSessionBound) return;
  provider.__hieswapSessionBound = true;
  provider.on("connect", (info) => {
    console.info("[WC:evm] connect", info?.chainId || info);
    provider.__hieswapSessionOpts?.onSessionConnect?.(info);
  });
  provider.on("session_event", (payload) => {
    console.info("[WC:evm] session_event", payload?.params?.event?.name || payload);
    const evt = payload?.params?.event;
    if (evt?.name === "accountsChanged" && evt?.data?.[0]) {
      provider.__hieswapSessionOpts?.onAccountsReady?.(normalizeAccount(evt.data[0]));
    }
    provider.__hieswapSessionOpts?.onSessionEvent?.(payload);
  });
  provider.on("disconnect", (err) => {
    console.info("[WC:evm] disconnect", err?.message || err);
    provider.__hieswapSessionOpts?.onSessionDisconnect?.(err);
  });
  provider.on("accountsChanged", (accounts) => {
    const acc = Array.isArray(accounts) ? accounts[0] : accounts;
    if (acc) provider.__hieswapSessionOpts?.onAccountsReady?.(normalizeAccount(acc));
  });
}

async function initWalletConnectShellOnce(chainId, options = {}) {
  const id = coerceChainId(chainId, WC_ANCHOR_CHAIN_ID);
  const showQrModal = options.showQrModal !== false;
  const silentRestore = options.silentRestore === true;
  const appKitTheme = showQrModal ? buildAppKitThemeOptions(options.themeMode) : undefined;
  const meta = resolveWalletConnectMetadata();

  if (wcInitInflight) {
    await wcInitInflight;
    /* AppKit 扫码必须全新 init；仅 silentRestore 可复用 shell */
    if (silentRestore && wcShellAuthorizesChain(wcProviderShell, id)) {
      bindProviderDisplayUri(wcProviderShell, options);
      bindProviderSessionCallbacks(wcProviderShell, options);
      return { provider: wcProviderShell, targetChainId: id };
    }
    await disposeActiveProvider({ resetInit: true, removeDom: showQrModal });
  }

  const run = async () => {
    if (silentRestore) {
      /* 刷新恢复：不能 clearWalletConnectStorage，否则 SDK 无法加载持久化 session */
      if (wcShellAuthorizesChain(wcProviderShell, id)) {
        activeProvider = wcProviderShell;
        return { provider: wcProviderShell, targetChainId: id };
      }
    } else {
      await disposeActiveProvider({ resetInit: true, removeDom: showQrModal });
    }

    if (meta.isLocalDev) {
      console.info(
        `[WC:evm] local dev origin=${meta.pageOrigin} metadata.url=${meta.url} — 若 QR 空白，请在 cloud.reown.com 为 project ${PROJECT_ID.slice(0, 8)}… 添加 Allowed Domain: ${meta.pageOrigin}`,
      );
    }

    const { requiredChains, optional } = buildWalletConnectNamespaceChains(id);
    const chainName = getChainFromId(id)?.name || String(id);
    console.info("[WC:evm] init provider", {
      targetChainId: id,
      chainName,
      requiredChains,
      optionalChains: optional,
      silentRestore,
    });
    console.warn(
      `%c[WC] SESSION PROPOSAL: required=[${id}] (${chainName}) — strict single-chain, no optional`,
      "color:red;font-size:14px;font-weight:bold",
    );

    const EthereumProvider = await loadEthereumProvider();

    const initOpts = {
      projectId: PROJECT_ID,
      chains: requiredChains,
      optionalChains: optional,
      showQrModal,
      relayUrl: DEFAULT_RELAY,
      disableProviderPing: true,
      rpcMap: buildRpcMapForChain(id),
      metadata: {
        name: "HieSwap Bridge",
        description: "Cross-chain Bridge",
        url: meta.url,
        icons: meta.icons,
      },
    };
    if (appKitTheme) initOpts.qrModalOptions = appKitTheme;

    const provider = await EthereumProvider.init(initOpts);
    wcProviderShell = provider;
    activeProvider = provider;
    if (showQrModal) ensureAppKitLayoutStyles();

    bindProviderDisplayUri(provider, options);
    bindProviderSessionCallbacks(provider, options);

    return { provider, targetChainId: id };
  };

  wcInitInflight = run();
  try {
    return await wcInitInflight;
  } finally {
    wcInitInflight = null;
  }
}

async function createWalletConnectProvider(chainId, options = {}) {
  const id = coerceChainId(chainId, WC_ANCHOR_CHAIN_ID);
  if (!EVM_CHAIN_IDS.includes(id)) {
    throw new Error(`WalletConnect unsupported chain: ${chainId}`);
  }

  const showQrModal = options.showQrModal !== false;
  /* 单链模式：仅当 session namespace 已授权目标源链时才复用 shell */
  const sessionMatchesChain = wcShellAuthorizesChain(wcProviderShell, id);
  const reuseLiveShell = sessionMatchesChain && options.reuseShell === true;
  const cancelledShell =
    options.reuseCancelledShell !== false ? pickCancelledWcShell(id) : null;

  if (cancelledShell && !reuseLiveShell) {
    activeProvider = cancelledShell;
    wcProviderShell = cancelledShell;
    if (cancelledShell.rpc) cancelledShell.rpc.showQrModal = showQrModal;
    if (showQrModal) {
      if (typeof document !== "undefined") {
        const stale = document.querySelector("w3m-modal, appkit-modal");
        if (stale && !stale.shadowRoot) stale.remove();
      }
      ensureAppKitLayoutStyles();
    } else {
      stopAppKitUiEnhancements();
    }
    bindProviderDisplayUri(cancelledShell, options);
    bindProviderSessionCallbacks(cancelledShell, options);
    console.info("[WC:evm] reuse cancelled shell", {
      targetChainId: id,
      chainName: getChainFromId(id)?.name || String(id),
    });
    return { provider: cancelledShell, targetChainId: id };
  }

  if (!reuseLiveShell && wcProviderShell) {
    const approved = getWalletConnectSessionChainIds(wcProviderShell);
    console.info('[WC:evm] reinit for chain', id, '(session approved:', approved, ')');
    await disposeActiveProvider({ resetInit: true, removeDom: showQrModal });
  }

  if (reuseLiveShell) {
    await disposeActiveProvider({ resetInit: false, keepSession: true });
    wcProviderShell.rpc.showQrModal = showQrModal;
    activeProvider = wcProviderShell;
    if (showQrModal) {
      if (typeof document !== "undefined") {
        const stale = document.querySelector("w3m-modal, appkit-modal");
        if (stale && !stale.shadowRoot) stale.remove();
      }
      ensureAppKitLayoutStyles();
    } else {
      stopAppKitUiEnhancements();
    }
    bindProviderDisplayUri(wcProviderShell, options);
    bindProviderSessionCallbacks(wcProviderShell, options);
    return { provider: wcProviderShell, targetChainId: id };
  }

  return initWalletConnectShellOnce(id, options);
}

async function finalizeWalletConnectSession(provider, targetChainId, options = {}) {
  const finishIfReady = () => {
    const built = buildWalletConnectConnectResult(provider, targetChainId);
    if (built?.account) {
      options.onAccountsReady?.(built.account);
      pinActiveWalletConnectProvider(provider);
      return built;
    }
    return null;
  };

  /* 无 account 时必须 enable()；二次扫码时残留 live session 不能跳过 enable */
  let ready = finishIfReady();
  if (ready) return ready;

  if (provider?.rpc?.showQrModal !== false) {
    if (typeof document !== "undefined" && !document.querySelector("w3m-modal, appkit-modal")) {
      await ensureW3mModalHost();
    }
    await prefetchAppKitWalletExplorer();
  }
  if (!options.skipAbortBeforeEnable && provider?.rpc?.showQrModal === false) {
    abortActivePairing(provider);
  }
  const enableRaw =
    options.useDiagnostics === false
      ? provider.enable()
      : enableWithUriDiagnostics(provider, "evm");
  if (enableRaw?.catch) {
    enableRaw.catch((err) => {
      if (!isWalletUserCancel(err)) {
        console.warn("[WC:evm] enable side reject", err?.message || err);
      }
    });
  }
  const enableTask = requestWithTimeout(enableRaw, 90000, "WC_ENABLE_TIMEOUT");
  wcEnableInflight = enableTask;
  try {
    await enableTask;
  } catch (err) {
    if (isWalletUserCancel(err)) throw err;
    ready = finishIfReady();
    if (ready) return ready;
    console.warn("[WC:evm] enable failed, read session", err?.message || err);
  } finally {
    if (wcEnableInflight === enableTask) wcEnableInflight = null;
  }

  ready = finishIfReady();
  if (ready) return ready;

  const fromSession = await waitForWalletConnectSessionAccounts(provider, targetChainId, {
    timeoutMs: options.sessionSyncTimeoutMs ?? 30000,
    skipRpcProbe: true,
    onAccount: (acc) => options.onAccountsReady?.(acc),
  });
  if (fromSession?.account) {
    pinActiveWalletConnectProvider(provider);
    return fromSession;
  }
  if (isEvmWalletConnectSessionLive(provider)) {
    assertWcChainAuthorized(provider, targetChainId);
  }
  throw new Error("WC_NO_ACCOUNT");
}

/**
 * Rosswap activate 前清理：只 disconnect + 清 storage + 补 w3m-modal。
 * 禁止 modal.close() — SDK 会抛 Connection request reset。
 */
/**
 * softClean=true：用户取消配对时的软清理，跳过 disconnect/clearStorage/cleanupDom，
 * 保留 provider 的 relay 连接和 AppKit modal 绑定，供复用。
 */
async function teardownForDesktopWalletConnect({ softClean = false, skipIfEmpty = false } = {}) {
  const p = activeProvider || wcProviderShell;
  if (skipIfEmpty && !p) {
    await ensureW3mModalHost();
    return;
  }
  detachEvmWalletConnectListeners();
  clearWalletConnectDisplayUriConsumer();
  wcProviderGeneration += 1;
  activeProvider = null;
  wcProviderShell = null;
  stopAppKitUiEnhancements();
  if (wcEnableInflight) {
    try {
      await Promise.race([wcEnableInflight, sleepMs(5000)]);
    } catch (_e) {
      void _e;
    }
    wcEnableInflight = null;
  }
  if (p && !softClean) {
    /* abort 进行中的配对让 enable() 快速失败（已在上方等待完毕，此处一般为 no-op） */
    try { p.signer?.abortPairingAttempt?.(); } catch (_e) { void _e; }
    try {
      await Promise.race([p.disconnect(), sleepMs(2500)]);
    } catch (_e) {
      void _e;
    }
  }
  resetAppKitCssState();
  if (!softClean) {
    await clearWalletConnectStorage();
    cleanupAppKitDom();
  }
  await ensureW3mModalHost();
}

/**
 * Rosswap / Pancake 桌面 WC：teardown → init → enable()，无 prepare/finalize/abort。
 * AppKit isInitialized 全局锁导致关窗后必须 ensureW3mModalHost，不能 cleanupAppKitDom。
 */
export async function openDesktopWalletConnectModal(chainId, options = {}) {
  const id = coerceChainId(chainId, WC_ANCHOR_CHAIN_ID);
  const mobile =
    options.mobile ??
    (typeof window !== "undefined" && window.matchMedia("(max-width: 899px)").matches);
  if (!EVM_CHAIN_IDS.includes(id)) {
    throw new Error(`WalletConnect unsupported chain: ${chainId}`);
  }

  const inflightKey = `${id}:${mobile ? "m" : "d"}`;
  if (options.forceNew) {
    await forceAbortWalletConnectAttempt({ closeModal: true, timeoutMs: 800 });
  } else if (desktopModalInflight && desktopModalInflightKey === inflightKey) {
    return desktopModalInflight;
  }

  supersedeInlineWalletConnectPairing();
  const gen = ++desktopActivateGen;

  const runPromise = desktopActivateQueue.then(async () => {
    const hasWcInflight = !!(wcInitInflight || wcEnableInflight);
    await awaitWalletConnectIdle({ timeoutMs: mobile || hasWcInflight ? 1200 : 800 });
    if (gen !== desktopActivateGen) {
      const err = new Error("WC_SUPERSEDED");
      err.code = "WC_SUPERSEDED";
      throw err;
    }
    /* 用户取消配对后，provider + AppKit modal 绑定仍有效，可直接复用。
       AppKit createAppKit 有全局 isInitialized 锁，二次 init 无法重新绑定 modal，
       复用旧 provider 直接 enable() 是唯一可靠方案。 */
    const cancelledShell = pickCancelledWcShell(id);

    await teardownForDesktopWalletConnect({ softClean: !!cancelledShell, skipIfEmpty: true });
    if (gen !== desktopActivateGen) {
      const err = new Error("WC_SUPERSEDED");
      err.code = "WC_SUPERSEDED";
      throw err;
    }

    ensureAppKitLayoutStyles();
    const meta = resolveWalletConnectMetadata();
    const { requiredChains, optional } = buildWalletConnectNamespaceChains(id);

    let provider;
    if (cancelledShell) {
      console.info("[WC:desktop] reuse shell after cancel", {
        targetChainId: id,
        chainName: getChainFromId(id)?.name || String(id),
      });
      provider = cancelledShell;
      wcProviderShell = provider;
      activeProvider = provider;
      if (provider.rpc) provider.rpc.showQrModal = true;
      await ensureW3mModalHost();
    } else {
      console.info("[WC:desktop] activate", {
        targetChainId: id,
        chainName: getChainFromId(id)?.name || String(id),
      });
      const EthereumProvider = await loadEthereumProvider();
      provider = await EthereumProvider.init({
        projectId: PROJECT_ID,
        chains: requiredChains,
        optionalChains: optional,
        showQrModal: true,
        relayUrl: DEFAULT_RELAY,
        disableProviderPing: true,
        rpcMap: buildRpcMapForChain(id),
        metadata: {
          name: "HieSwap Bridge",
          description: "Cross-chain Bridge",
          url: meta.url,
          icons: meta.icons,
        },
        qrModalOptions: buildAppKitThemeOptions(options.themeMode),
      });

      if (gen !== desktopActivateGen) {
        try {
          await provider.disconnect();
        } catch (_e) {
          void _e;
        }
        const err = new Error("WC_SUPERSEDED");
        err.code = "WC_SUPERSEDED";
        throw err;
      }

      wcProviderShell = provider;
      activeProvider = provider;
    }

    bindProviderDisplayUri(provider, options);
    bindProviderSessionCallbacks(provider, options);

    void prefetchAppKitWalletExplorer();
    if (gen !== desktopActivateGen) {
      const err = new Error("WC_SUPERSEDED");
      err.code = "WC_SUPERSEDED";
      throw err;
    }

    /* init 已注入 w3m-modal；此处再 ensure 会误删未挂载 shadow 的新节点 */
    const enableTask = requestWithTimeout(provider.enable(), 90000, "WC_ENABLE_TIMEOUT");
    wcEnableInflight = enableTask;
    let accounts;
    try {
      accounts = await enableTask;
    } catch (err) {
      if (isWalletUserCancel(err)) throw err;
      const built = buildWalletConnectConnectResult(provider, id);
      if (built?.account) {
        pinActiveWalletConnectProvider(provider);
        return built;
      }
      throw err;
    } finally {
      if (wcEnableInflight === enableTask) wcEnableInflight = null;
    }

    if (!buildWalletConnectConnectResult(provider, id)?.account) {
      openAppKitModalIfNeeded(provider);
    }

    if (gen !== desktopActivateGen) {
      const err = new Error("WC_SUPERSEDED");
      err.code = "WC_SUPERSEDED";
      throw err;
    }

    let built = buildWalletConnectConnectResult(provider, id);
    if (!built?.account) {
      const acc = normalizeAccount(Array.isArray(accounts) ? accounts[0] : accounts);
      if (acc) {
        if (!Array.isArray(provider.accounts) || !provider.accounts.length) {
          provider.accounts = [acc];
        }
        built = buildWalletConnectConnectResult(provider, id);
      }
    }
    if (!built?.account) {
      built = await waitForWalletConnectSessionAccounts(provider, id, {
        timeoutMs: options.sessionSyncTimeoutMs ?? 15000,
        skipRpcProbe: true,
        onAccount: (acc) => options.onAccountsReady?.(acc),
      });
    }
    if (!built?.account) {
      throw new Error("WC_NO_ACCOUNT");
    }
    pinActiveWalletConnectProvider(provider);
    options.onAccountsReady?.(built.account);
    return built;
  });

  desktopActivateQueue = runPromise.then(
    () => {},
    () => {},
  );
  desktopModalInflight = runPromise;
  desktopModalInflightKey = inflightKey;
  try {
    return await runPromise;
  } finally {
    if (desktopModalInflight === runPromise) {
      desktopModalInflight = null;
      desktopModalInflightKey = "";
    }
  }
}

/**
 * Uniswap 式 EVM WC 连接：单例 shell → enable（如需）→ 读 session 账户。
 * showQrModal:true 走 Pancake 式桌面弹窗；false 时由 onDisplayUri 展示二维码（AVE 安装引导）。
 */
export async function connectEvmWalletConnect(chainId, options = {}) {
  if (options.showQrModal !== false) {
    return openDesktopWalletConnectModal(chainId, options);
  }
  return enqueueWcPairing(async () => {
    const { provider, targetChainId } = await createWalletConnectProvider(chainId, {
      ...options,
      showQrModal: false,
      reuseShell: options.reuseShell === true,
    });
    try {
      return await finalizeWalletConnectSession(provider, targetChainId, options);
    } catch (err) {
      if (!isWalletUserCancel(err) && !isEvmWalletConnectSessionLive(provider)) {
        await disposeActiveProvider({ resetInit: false });
      }
      throw err;
    }
  });
}

export async function initConnect(chainId, options = {}) {
  return connectEvmWalletConnect(chainId, { ...options, showQrModal: true });
}

export async function beginWalletConnectPairing(chainId, options = {}) {
  const gen = inlinePairingGen;
  return enqueueWcPairing(async () => {
    if (gen !== inlinePairingGen) {
      const err = new Error("WC_SUPERSEDED");
      err.code = "WC_SUPERSEDED";
      throw err;
    }
    const { provider, targetChainId } = await createWalletConnectProvider(chainId, {
      ...options,
      showQrModal: false,
      reuseShell: false,
    });
    if (gen !== inlinePairingGen) {
      const err = new Error("WC_SUPERSEDED");
      err.code = "WC_SUPERSEDED";
      throw err;
    }

    const enablePromise = finalizeWalletConnectSession(provider, targetChainId, {
      ...options,
      pairingMode: true,
    }).catch(async (err) => {
      if (err?.code === "WC_SUPERSEDED" || isWalletUserCancel(err)) throw err;
      if (!isEvmWalletConnectSessionLive(provider)) {
        await disposeActiveProvider({ resetInit: false });
      }
      throw err;
    });

    return { provider, enablePromise };
  });
}

/** 新一轮 AVE/WalletConnect 扫码前：等待 inflight 结束并清 shell */
export async function prepareWalletConnectPairing(options = {}) {
  const { removeDom = false } = options;
  await awaitWalletConnectIdle({ timeoutMs: 5000 });
  if (typeof window !== "undefined") {
    window.__hieswapWcLastUri = "";
    window.__hieswapWcLastUriGen = 0;
  }
  clearWalletConnectDisplayUriConsumer();
  await disposeActiveProvider({ resetInit: true, removeDom });
}

/**
 * 安装引导内嵌 QR（showQrModal:false）：软清理可复用 shell，避免 AppKit 二次 init 失效。
 */
export async function prepareInlineWalletConnectPairing(targetChainId, options = {}) {
  const { quick = false } = options;
  await awaitWalletConnectIdle({ timeoutMs: quick ? 800 : 2500 });
  if (typeof window !== "undefined") {
    window.__hieswapWcLastUri = "";
    window.__hieswapWcLastUriGen = 0;
  }
  return teardownForInlineWalletConnect({ targetChainId, forceFullReset: false });
}

/**
 * 从安装引导内嵌 QR 切到桌面 AppKit 弹窗：中止 inline enable，保留 shell，showQrModal=true。
 */
export async function prepareDesktopWalletConnectPairing(targetChainId) {
  supersedeInlineWalletConnectPairing();
  await awaitWalletConnectIdle({ timeoutMs: 5000 });
  if (typeof window !== "undefined") {
    window.__hieswapWcLastUri = "";
    window.__hieswapWcLastUriGen = 0;
  }
  clearWalletConnectDisplayUriConsumer();
  detachEvmWalletConnectListeners();

  const id = coerceChainId(targetChainId, WC_ANCHOR_CHAIN_ID);
  const shell = wcProviderShell;

  if (shell && isEvmWalletConnectSessionLive(shell)) {
    if (shell.rpc) shell.rpc.showQrModal = true;
    activeProvider = shell;
    ensureAppKitLayoutStyles();
    await ensureW3mModalHost();
    return shell;
  }

  if (shell) {
    const shellChain = coerceChainId(shell.chainId, 0);
    if (shellChain && shellChain !== id) {
      await disposeActiveProvider({ resetInit: true, removeDom: false, closeModal: false });
      await ensureW3mModalHost();
      return null;
    }
    if (wcEnableInflight) {
      try {
        await Promise.race([wcEnableInflight, sleepMs(3500)]);
      } catch (_e) {
        void _e;
      }
      wcEnableInflight = null;
    }
    abortActivePairing(shell, { closeModal: false });
    activeProvider = shell;
    wcProviderShell = shell;
    if (shell.rpc) shell.rpc.showQrModal = true;
    stopAppKitUiEnhancements();
    resetAppKitCssState();
    ensureAppKitLayoutStyles();
    await ensureW3mModalHost();
    return shell;
  }

  await ensureW3mModalHost();
  return null;
}

export async function cancelWalletConnectPairing(options = {}) {
  const { removeDom = false, resetInit = false, closeModal = false, fast = false, soft = false } =
    options;
  if (soft) {
    await forceAbortWalletConnectAttempt({ closeModal, timeoutMs: closeModal ? 800 : 600 });
    detachEvmWalletConnectListeners();
    clearWalletConnectDisplayUriConsumer();
    stopAppKitUiEnhancements();
    resetAppKitCssState();
    if (wcProviderShell) activeProvider = wcProviderShell;
    return;
  }
  if (fast) {
    await forceAbortWalletConnectAttempt({ closeModal, timeoutMs: closeModal ? 800 : 600 });
  } else {
    await awaitWalletConnectIdle({ timeoutMs: resetInit ? 5000 : 3500 });
    if (closeModal) {
      const p = activeProvider || wcProviderShell;
      if (p) abortActivePairing(p, { closeModal: true });
    }
  }
  await disposeActiveProvider({ resetInit, removeDom, closeModal });
}

export async function resetWalletConnectProvider() {
  await disposeActiveProvider({ resetInit: true, removeDom: true });
}

/** 用户切换发起链且尚未连钱包时，清掉链不匹配的 WC shell，避免下次扫码复用 BSC session */
export async function invalidateWcShellIfChainMismatch(targetChainId) {
  const id = coerceChainId(targetChainId, WC_ANCHOR_CHAIN_ID);
  const shell = wcProviderShell;
  if (!shell) return;

  if (isEvmWalletConnectSessionLive(shell)) {
    if (!wcShellAuthorizesChain(shell, id)) {
      console.info("[WC:evm] clear shell after from-chain change", {
        target: id,
        approved: getWalletConnectSessionChainIds(shell),
      });
      await disposeActiveProvider({ resetInit: true, removeDom: false });
    }
    return;
  }

  const shellChain = coerceChainId(shell.chainId, 0);
  if (shellChain && shellChain !== id) {
    console.info("[WC:evm] clear shell after chain target change", {
      target: id,
      shellChain,
    });
    await disposeActiveProvider({ resetInit: true, removeDom: false });
  }
}

let restoreInflight = null;

/** 刷新后从 WC SDK 持久化 session 静默恢复（不弹 QR、不清 storage） */
async function createRestoreWalletConnectProvider(chainId) {
  const id = coerceChainId(chainId, WC_ANCHOR_CHAIN_ID);
  if (!EVM_CHAIN_IDS.includes(id)) {
    throw new Error(`WalletConnect unsupported chain: ${chainId}`);
  }

  const live = getActiveWalletConnectProvider();
  if (live && wcShellAuthorizesChain(live, id)) {
    return { provider: live, targetChainId: id };
  }

  if (wcProviderShell && !isEvmWalletConnectSessionLive(wcProviderShell)) {
    activeProvider = null;
    wcProviderShell = null;
  }

  return initWalletConnectShellOnce(id, {
    showQrModal: false,
    silentRestore: true,
  });
}

export async function tryRestoreEvmWalletConnect(targetChainId) {
  if (typeof window === "undefined") return null;
  if (db.get("wallet_type") !== "wallet_connect") return null;

  const topic = String(db.get("topic", "") || "").trim();
  const cid = coerceChainId(
    targetChainId || db.get("chainId", 0) || WC_ANCHOR_CHAIN_ID,
    WC_ANCHOR_CHAIN_ID,
  );

  const live = getActiveWalletConnectProvider();
  if (live && wcShellAuthorizesChain(live, cid)) {
    const built = buildWalletConnectConnectResult(live, cid);
    if (built?.account) return built;
  }

  if (!topic) return null;
  if (restoreInflight) return restoreInflight;

  restoreInflight = (async () => {
    try {
      const { provider } = await createRestoreWalletConnectProvider(cid);
      if (!isEvmWalletConnectSessionLive(provider)) {
        console.info("[WC:evm] restore: no persisted session");
        return null;
      }
      let built = buildWalletConnectConnectResult(provider, cid);
      if (!built?.account) {
        built = await waitForWalletConnectSessionAccounts(provider, cid, {
          timeoutMs: 15000,
        });
      }
      if (!built?.account) return null;
      if (!wcSessionHasChain(provider, cid)) {
        console.info("[WC:evm] restore: session missing target chain", {
          wanted: cid,
          approved: getWalletConnectSessionChainIds(provider),
        });
        return null;
      }
      console.info("[WC:evm] restore ok", String(built.account).slice(0, 10));
      return built;
    } catch (err) {
      console.warn("[WC:evm] restore failed", err);
      return null;
    } finally {
      restoreInflight = null;
    }
  })();

  return restoreInflight;
}

export const getAccountWalletConnect = async (chainId, options = {}) => {
  const prevType = db.get("wallet_type", "inject");
  db.set("wallet_type", "wallet_connect");
  try {
    return await openDesktopWalletConnectModal(chainId, options);
  } catch (err) {
    db.set("wallet_type", isWalletUserCancel(err) || err?.code === "WC_SUPERSEDED" ? prevType : "inject");
    throw err;
  }
};
